import type { ApiClient } from '../../../shared/api/api-client';
import { ApiError } from '../../../shared/api/api-error';
import type { ScheduleSlotListRow } from './schedule-slots.types';

export type FetchScheduleSlotsParams = {
  organization_id: number;
  from_date: string;
  to_date: string;
  branch_id?: number | null;
  service_id?: number | null;
};

type OrganizationScopeParams = {
  organization_id: number;
};

function buildOrganizationScopeQuery(params: OrganizationScopeParams): string {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  return `?${sp.toString()}`;
}

function assertYmdDate(value: string, field: string): void {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error(`${field} must be YYYY-MM-DD`);
  }
}

function parseSlotRow(raw: unknown): ScheduleSlotListRow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'number' ? r.id : Number(r.id);
  const service_id = typeof r.service_id === 'number' ? r.service_id : Number(r.service_id);
  const service_name = typeof r.service_name === 'string' ? r.service_name : '';
  const service_type_slug = typeof r.service_type_slug === 'string' ? r.service_type_slug : '';
  const service_type_name =
    r.service_type_name === null || r.service_type_name === undefined
      ? null
      : typeof r.service_type_name === 'string'
        ? r.service_type_name
        : null;
  const service_color =
    r.service_color === null || r.service_color === undefined
      ? null
      : typeof r.service_color === 'string'
        ? r.service_color
        : null;
  const service_type_color =
    r.service_type_color === null || r.service_type_color === undefined
      ? null
      : typeof r.service_type_color === 'string'
        ? r.service_type_color
        : null;
  const service_group_type =
    r.service_group_type === null || r.service_group_type === undefined
      ? null
      : typeof r.service_group_type === 'string'
        ? r.service_group_type
        : null;
  const slot_date = typeof r.slot_date === 'string' ? r.slot_date : '';
  const start_time = typeof r.start_time === 'string' ? r.start_time : '';
  const end_time = typeof r.end_time === 'string' ? r.end_time : '';
  const max_participants =
    typeof r.max_participants === 'number' ? r.max_participants : Number(r.max_participants);
  const places_left = typeof r.places_left === 'number' ? r.places_left : Number(r.places_left);
  if (!Number.isFinite(id) || id < 1) return null;
  if (!Number.isFinite(service_id) || service_id < 1) return null;
  if (!slot_date || !start_time || !end_time) return null;
  if (!Number.isFinite(max_participants) || max_participants < 1) return null;
  if (!Number.isFinite(places_left) || places_left < 0) return null;

  /** Канон API — `trainer_user_id` (users.id в орг.); `trainer_id` — только совместимость со старыми ответами. */
  const tidRaw = r.trainer_user_id ?? r.trainer_id;
  const trainer_id =
    tidRaw === null || tidRaw === undefined ? null : typeof tidRaw === 'number' ? tidRaw : Number(tidRaw);
  const trainer_name =
    r.trainer_name === null || r.trainer_name === undefined
      ? null
      : typeof r.trainer_name === 'string'
        ? r.trainer_name
        : null;

  const bid = r.branch_id;
  const branch_id =
    bid === null || bid === undefined
      ? null
      : typeof bid === 'number'
        ? bid
        : Number(bid);
  const b = branch_id != null && Number.isFinite(branch_id) ? (branch_id as number) : null;

  const rs = r.recurring_series_id;
  let recurring_series_id: number | null | undefined;
  if (rs === null || rs === undefined) {
    recurring_series_id = undefined;
  } else if (typeof rs === 'number' && Number.isFinite(rs)) {
    recurring_series_id = rs;
  } else {
    const n = Number(rs);
    recurring_series_id = Number.isFinite(n) && n > 0 ? n : undefined;
  }

  const space_id = r.space_id === null || r.space_id === undefined ? null : Number(r.space_id);
  const space_name = typeof r.space_name === 'string' ? r.space_name : null;
  const space_capacity = r.space_capacity === null || r.space_capacity === undefined ? null : Number(r.space_capacity);
  const self_service_capacity =
    r.self_service_capacity === null || r.self_service_capacity === undefined ? null : Number(r.self_service_capacity);
  const self_service_used =
    r.self_service_used === null || r.self_service_used === undefined ? 0 : Number(r.self_service_used);
  const capacity_load_factor =
    typeof r.capacity_load_factor === 'number' ? r.capacity_load_factor : Number(r.capacity_load_factor ?? 1);
  const blocks_space_fully = Boolean(r.blocks_space_fully);
  const is_all_day = Boolean(r.is_all_day);
  const is_rental = Boolean(r.is_rental);
  const status: 'active' | 'cancelled' = r.status === 'cancelled' ? 'cancelled' : 'active';

  return {
    id,
    service_id,
    service_name,
    service_type_slug,
    service_type_name,
    service_color,
    service_type_color,
    service_group_type,
    slot_date,
    start_time,
    end_time,
    max_participants,
    is_available: Boolean(r.is_available),
    status,
    places_left,
    trainer_id: trainer_id != null && Number.isFinite(trainer_id) ? (trainer_id as number) : null,
    trainer_name,
    branch_id: b,
    space_id: Number.isFinite(space_id) && space_id! > 0 ? space_id : null,
    space_name,
    space_capacity: Number.isFinite(space_capacity) ? space_capacity : null,
    self_service_capacity: Number.isFinite(self_service_capacity) ? self_service_capacity : null,
    self_service_used: Number.isFinite(self_service_used) ? self_service_used : 0,
    capacity_load_factor: Number.isFinite(capacity_load_factor) && capacity_load_factor! >= 1 ? capacity_load_factor : 1,
    blocks_space_fully,
    is_all_day,
    is_rental,
    ...(recurring_series_id !== undefined ? { recurring_series_id } : {})
  };
}

/**
 * `GET /api/v1/schedule/slots` — массив слотов; для staff обязательно передаём `organization_id` из контекста орг.
 */
export async function fetchScheduleSlots(
  client: ApiClient,
  params: FetchScheduleSlotsParams
): Promise<ScheduleSlotListRow[]> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  sp.set('from_date', params.from_date);
  sp.set('to_date', params.to_date);
  // Сотрудникам с правом на расписание/записи сервер отдаёт и закрытые для записи слоты
  // (is_available=false): занятие проводится и занимает тренера/зал, поэтому оно должно быть видно
  // в сетке. Без права backend игнорирует флаг и отдаёт только доступные (как клиентам).
  sp.set('manage', '1');
  // include_cancelled=1 — только staff SPA: backend учитывает флаг вместе с manage и правом
  // расписания/записей; клиентские приложения его не шлют и отменённых не видят.
  sp.set('include_cancelled', '1');
  if (params.branch_id != null && params.branch_id > 0) {
    sp.set('branch_id', String(params.branch_id));
  }
  if (params.service_id != null && params.service_id > 0) {
    sp.set('service_id', String(params.service_id));
  }
  const path = `/schedule/slots?${sp.toString()}`;
  const data = await client.request<unknown>(path, { method: 'GET' });
  if (!Array.isArray(data)) return [];
  const out: ScheduleSlotListRow[] = [];
  for (const row of data) {
    const p = parseSlotRow(row);
    if (p) out.push(p);
  }
  return out;
}

export type FetchScheduleSlotDetailParams = {
  organization_id: number;
  slot_id: number;
};

/** `GET /api/v1/schedule/slots/{slot_id}` — один слот (read-only карточка). */
export async function fetchScheduleSlotDetail(
  client: ApiClient,
  params: FetchScheduleSlotDetailParams
): Promise<ScheduleSlotListRow | null> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  // Тот же явный флаг: карточка отменённого занятия открывается только staff с правом.
  sp.set('include_cancelled', '1');
  const path = `/schedule/slots/${params.slot_id}?${sp.toString()}`;
  const data = await client.request<unknown>(path, { method: 'GET' });
  return parseSlotRow(data);
}

export type MoveScheduleSlotParams = {
  organization_id: number;
  slot_id: number;
  slot_date: string;
  start_time: string;
  end_time: string;
};

/** `POST /api/v1/schedule/slots/{slot_id}/move` — тело: slot_date, start_time, end_time (HH:MM). */
export async function moveScheduleSlot(client: ApiClient, params: MoveScheduleSlotParams): Promise<void> {
  assertYmdDate(params.slot_date, 'slot_date');
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  const path = `/schedule/slots/${params.slot_id}/move?${sp.toString()}`;
  await client.request<unknown>(path, {
    method: 'POST',
    body: {
      slot_date: params.slot_date,
      start_time: params.start_time,
      end_time: params.end_time
    }
  });
}

export type ValidateScheduleSlotMoveParams = MoveScheduleSlotParams & { signal?: AbortSignal };

export type ScheduleSlotMoveViolation = {
  code: string;
  msg: string;
  related_slot_ids?: number[];
};

export type ScheduleSlotMoveValidateResult =
  | { ok: true }
  | { ok: false; violations: ScheduleSlotMoveViolation[] };

/** `POST /api/v1/schedule/slots/{slot_id}/move/validate` — проверки без записи; при 422 — список нарушений. */
export async function validateScheduleSlotMove(
  client: ApiClient,
  params: ValidateScheduleSlotMoveParams
): Promise<ScheduleSlotMoveValidateResult> {
  assertYmdDate(params.slot_date, 'slot_date');
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  const path = `/schedule/slots/${params.slot_id}/move/validate?${sp.toString()}`;
  try {
    await client.request<unknown>(path, {
      method: 'POST',
      body: {
        slot_date: params.slot_date,
        start_time: params.start_time,
        end_time: params.end_time,
      },
      signal: params.signal,
    });
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError && e.status === 422 && e.detailItems && e.detailItems.length > 0) {
      return {
        ok: false,
        violations: e.detailItems.map((it) => ({
          code: String(it.type ?? 'unknown'),
          msg: it.msg,
          related_slot_ids: it.related_slot_ids,
        })),
      };
    }
    throw e;
  }
}

export type MutateScheduleSlotParams = {
  organization_id: number;
  slot_id: number;
  scope?: 'single' | 'future' | 'series' | 'dissolve';
};

export type CancelScheduleSlotParams = MutateScheduleSlotParams & {
  notify?: boolean;
  notify_message?: string;
};

/** `POST /api/v1/schedule/slots/{slot_id}/cancel` — отменить занятие без удаления строки слота. */
export async function cancelScheduleSlot(client: ApiClient, params: CancelScheduleSlotParams): Promise<void> {
  const path = `/schedule/slots/${params.slot_id}/cancel${buildOrganizationScopeQuery(params)}`;
  const body: Record<string, unknown> = {};
  if (params.notify !== undefined) body.notify = params.notify;
  if (params.notify_message !== undefined) body.notify_message = params.notify_message;
  const init: Record<string, unknown> = { method: 'POST' };
  if (Object.keys(body).length > 0) init.body = body;
  await client.request<unknown>(path, init);
}

/** `DELETE /api/v1/schedule/slots/{slot_id}` — удалить слот; backend вернёт 409, если есть активные записи. */
export async function deleteScheduleSlot(client: ApiClient, params: MutateScheduleSlotParams): Promise<void> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  if (params.scope) {
    sp.set('scope', params.scope);
  }
  const path = `/schedule/slots/${params.slot_id}?${sp.toString()}`;
  await client.request<unknown>(path, { method: 'DELETE' });
}

/**
 * Тело `POST /api/v1/schedule/slots` — поля как в `domain.ScheduleSlotWrite` (JSON-ключи с заглавной буквы,
 * иначе Go-декодер не заполнит структуру).
 */
export type ScheduleSlotCreateBody = {
  ServiceID: number;
  TrainerUserID?: number;
  BranchID?: number;
  SlotDate: string;
  StartTime: string;
  EndTime: string;
  MaxParticipants: number;
  IsAvailable: boolean;
  /** Аренда: весь день, время вычисляет backend. */
  AllDay?: boolean;
};

export type ScheduleRecurringCreateBody = {
  ServiceID: number;
  TrainerUserID?: number;
  BranchID?: number;
  StartDate: string;
  EndDate: string;
  StartTime: string;
  EndTime: string;
  MaxParticipants: number;
  IsAvailable: boolean;
  Repeat: string;
  Weekdays: number[];
  /** Аренда: весь день, окно каждой даты вычисляет backend. */
  AllDay?: boolean;
};

/** `POST /api/v1/schedule/slots` — создание слота; ответ — один объект как в списке слотов. */
export async function createScheduleSlot(
  client: ApiClient,
  params: OrganizationScopeParams & { body: ScheduleSlotCreateBody }
): Promise<ScheduleSlotListRow | null> {
  const data = await client.request<unknown>(`/schedule/slots${buildOrganizationScopeQuery(params)}`, {
    method: 'POST',
    body: params.body,
  });
  return parseSlotRow(data);
}

/** `POST /api/v1/schedule/slots/recurring` — создание повторяемой серии слотов. */
export async function createScheduleRecurringSlots(
  client: ApiClient,
  params: OrganizationScopeParams & { body: ScheduleRecurringCreateBody }
): Promise<{ created_slots: number; recurring_series_id: number } | null> {
  const data = await client.request<unknown>(`/schedule/slots/recurring${buildOrganizationScopeQuery(params)}`, {
    method: 'POST',
    body: params.body,
  });
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const raw = data as Record<string, unknown>;
  const created = typeof raw.created_slots === 'number' ? raw.created_slots : Number(raw.created_slots);
  const series = typeof raw.recurring_series_id === 'number' ? raw.recurring_series_id : Number(raw.recurring_series_id);
  if (!Number.isFinite(created) || !Number.isFinite(series)) return null;
  return { created_slots: created, recurring_series_id: series };
}

/**
 * Тело `PATCH /api/v1/schedule/slots/{slot_id}` — частичное обновление (`domain.ScheduleSlotPatch`), ключи PascalCase.
 * Передавайте только поля, которые нужно изменить (кроме полного сценария формы — см. вызывающий код).
 */
export interface RecurringSeriesDetailSlot {
  id: number;
  slot_date: string;
  start_time: string;
  end_time: string;
  service_name: string;
  max_participants: number;
  bookings_count: number;
  is_available: boolean;
  status: string;
}

export interface RecurringSeriesDetail {
  id: number;
  organization_id: number;
  branch_id?: number | null;
  service_id: number;
  service_name: string;
  trainer_user_id?: number | null;
  trainer_name?: string;
  start_time: string;
  end_time: string;
  max_participants: number;
  repeat_rule: string;
  weekdays: number[];
  start_date: string;
  end_date: string;
  slots: RecurringSeriesDetailSlot[];
}

export async function getScheduleRecurringSeries(
  client: ApiClient,
  params: OrganizationScopeParams & { series_id: number }
): Promise<RecurringSeriesDetail | null> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  const data = await client.request<unknown>(`/schedule/recurring-series/${params.series_id}?${sp.toString()}`, {
    method: 'GET',
  });
  if (!data || typeof data !== 'object') return null;
  return data as RecurringSeriesDetail;
}

export async function patchScheduleSlot(
  client: ApiClient,
  params: OrganizationScopeParams & {
    slot_id: number;
    patch: Record<string, unknown>;
    detach_from_recurring?: boolean;
    series_scope?: 'single' | 'dissolve' | 'all';
  }
): Promise<ScheduleSlotListRow | null> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  if (params.detach_from_recurring) {
    sp.set('detach_from_recurring', '1');
  }
  if (params.series_scope) {
    sp.set('series_scope', params.series_scope);
  }
  const path = `/schedule/slots/${params.slot_id}?${sp.toString()}`;
  const data = await client.request<unknown>(path, { method: 'PATCH', body: params.patch });
  return parseSlotRow(data);
}

export async function patchScheduleRecurringSeriesGeometry(
  client: ApiClient,
  params: OrganizationScopeParams & {
    series_id: number;
    slot_id: number;
    slot_date: string;
    start_time: string;
    end_time: string;
  }
): Promise<{ updated_slots: number } | null> {
  const sp = new URLSearchParams();
  sp.set('organization_id', String(params.organization_id));
  const data = await client.request<unknown>(`/schedule/recurring-series/${params.series_id}/geometry?${sp.toString()}`, {
    method: 'PATCH',
    body: {
      slot_id: params.slot_id,
      slot_date: params.slot_date,
      start_time: params.start_time,
      end_time: params.end_time,
    },
  });
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const updated = Number((data as Record<string, unknown>).updated_slots);
  if (!Number.isFinite(updated)) return null;
  return { updated_slots: updated };
}
